import { useEffect, useState } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { Home, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { recoverFromResourceLoadError, isDynamicImportError } from '@/utils/versionCheck';

type RecoveryState = 'idle' | 'checking' | 'updated' | 'failed';

function getErrorMessage(error: unknown) {
  if (isRouteErrorResponse(error)) {
    return `${error.status} ${error.statusText}`;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String(error.message);
  }

  return String(error ?? '未知错误');
}

/** 数据路由错误页，避免懒加载失败时展示 React Router 默认开发错误页。 */
export function RouteErrorElement() {
  const error = useRouteError();
  const isResourceError = isDynamicImportError(error);
  const [recoveryState, setRecoveryState] = useState<RecoveryState>(
    isResourceError ? 'checking' : 'idle'
  );

  useEffect(() => {
    if (!isResourceError) {
      return;
    }

    let active = true;
    void recoverFromResourceLoadError(error).then(({ hasUpdate, reloaded }) => {
      if (!active || reloaded) {
        return;
      }

      setRecoveryState(hasUpdate ? 'updated' : 'failed');
    });

    return () => {
      active = false;
    };
  }, [error, isResourceError]);

  const isChecking = recoveryState === 'checking';
  const hasUpdate = recoveryState === 'updated';

  const handleReload = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.hash = '#/';
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background p-8 text-foreground">
      <div className="max-w-md space-y-6 text-center">
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold tracking-tight">
            {isChecking ? '正在检查页面资源' : hasUpdate ? '发现新版本' : '页面出现错误'}
          </h1>
          <p className="text-muted-foreground">
            {isChecking
              ? '页面资源加载异常，正在检查是否需要更新。'
              : hasUpdate
                ? '检测到页面资源已更新，请刷新页面后继续。'
                : isResourceError
                  ? '页面资源加载失败，请刷新页面后重试。'
                  : '抱歉，页面出现了意外错误。您可以尝试刷新页面或返回首页。'}
          </p>
        </div>

        <div className="flex flex-col justify-center gap-3 sm:flex-row">
          <Button onClick={handleReload} className="flex items-center gap-2">
            <RotateCcw className="h-4 w-4" />
            刷新页面
          </Button>
          <Button
            variant="outline"
            onClick={handleGoHome}
            className="flex items-center gap-2"
          >
            <Home className="h-4 w-4" />
            返回首页
          </Button>
        </div>

        {import.meta.env.MODE === 'development' && (
          <details className="w-full text-left">
            <summary className="mb-4 cursor-pointer text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
              点击查看详细错误信息
            </summary>
            <pre className="overflow-auto rounded-lg border bg-muted p-4 text-xs text-muted-foreground">
              {getErrorMessage(error)}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}
